/**
 * Iter208.76 — Public "Atrium" Site (Agenzia Immobiliare, editorial luxury).
 *
 * Serve 4 pagine pubbliche con lo stesso header/footer/CSS variables:
 *   - /site/:slug                        → Home (hero + listings + values + stats + about + testimonial + cta)
 *   - /site/:slug/annunci                → Griglia completa immobili con filtri (città + tipologia)
 *   - /site/:slug/annunci/:pid           → Dettaglio annuncio + form contatto email
 *   - /site/:slug/chi-siamo              → Storia + valori + team
 *   - /site/:slug/contatti               → Form contatto + recapiti + mappa
 *
 * Dati dinamici dal profilo pro (backend `/public/site/:slug`):
 *   - nome_agenzia, anno_fondazione, indirizzo, telefono, email, città
 *   - featured_properties = ultimi 3 immobili pubblicati
 *
 * Palette-widget rimosso (i colori si gestiscono dallo step 2 del wizard).
 * NB: editor inline (input on-click / tasti X per sezioni) sarà aggiunto in
 * Iter208.77 quando servirà anche il modello dati per salvare le edit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
import { Editable, Removable } from "@/lib/siteEditing";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
//  CSS (iniettato una volta, scoped via classe .atrium-site sul root)
// ============================================================================
const ATRIUM_CSS = `
.atrium-site {
  --ink:#14171A;
  --ink-2:#1D2124;
  --paper:#F3EFE6;
  --linen:#E9E3D6;
  --verdigris:#6E8C7C;
  --verdigris-dim:#4F6A5C;
  --clay:#A9744F;
  --graphite:#3E4143;
  --smoke:#AEB4B0;
  --line-dark:#2B3033;
  --line-light:#D9D2C1;
  --radius:2px;
  margin:0;background:var(--paper);color:var(--graphite);
  font-family:'Archivo', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing:antialiased;
}
.atrium-site *{box-sizing:border-box;}
.atrium-site h1,.atrium-site h2,.atrium-site h3{font-family:'Fraunces', Georgia, serif;font-weight:480;color:var(--ink);text-wrap:balance;margin:0;}
.atrium-site .mono{font-family:'IBM Plex Mono', 'SF Mono', monospace;font-variant-numeric:tabular-nums;}
.atrium-site .eyebrow{font-family:'IBM Plex Mono', monospace;font-size:0.72rem;letter-spacing:0.14em;text-transform:uppercase;color:var(--verdigris-dim);}
.atrium-site a{color:inherit;}
.atrium-site img{max-width:100%;display:block;}
.atrium-site .wrap{max-width:1180px;margin:0 auto;padding:0 32px;}
.atrium-site section{position:relative;}
.atrium-site header{position:sticky;top:0;z-index:50;background:rgba(243,239,230,0.86);backdrop-filter:blur(10px);border-bottom:1px solid var(--line-light);}
.atrium-site .nav{display:flex;align-items:center;justify-content:space-between;padding:20px 32px;max-width:1180px;margin:0 auto;}
.atrium-site .logo{font-family:'Fraunces', serif;font-size:1.32rem;font-weight:600;letter-spacing:0.01em;color:var(--ink);text-decoration:none;}
.atrium-site .logo span{color:var(--verdigris);}
.atrium-site .navlinks{display:flex;gap:36px;list-style:none;margin:0;padding:0;}
.atrium-site .navlinks a{text-decoration:none;font-size:0.92rem;color:var(--graphite);position:relative;padding-bottom:4px;}
.atrium-site .navlinks a::after{content:"";position:absolute;left:0;bottom:0;width:0;height:1px;background:var(--verdigris);transition:width .25s ease;}
.atrium-site .navlinks a:hover::after,.atrium-site .navlinks a.active::after{width:100%;}
.atrium-site .btn{display:inline-flex;align-items:center;gap:8px;background:var(--ink);color:var(--paper);padding:11px 22px;border-radius:var(--radius);font-size:0.86rem;text-decoration:none;border:1px solid var(--ink);transition:background .2s ease, color .2s ease;cursor:pointer;}
.atrium-site .btn:hover{background:var(--verdigris-dim);border-color:var(--verdigris-dim);}
.atrium-site .btn-ghost{background:transparent;color:var(--paper);border:1px solid rgba(243,239,230,0.35);}
.atrium-site .btn-ghost:hover{background:rgba(243,239,230,0.1);border-color:var(--paper);}
.atrium-site .hero{background:var(--ink);color:var(--paper);display:grid;grid-template-columns:1.05fr 0.95fr;align-items:stretch;min-height:640px;}
.atrium-site .hero-copy{padding:96px 56px 64px 32px;display:flex;flex-direction:column;justify-content:center;gap:28px;max-width:560px;margin-left:auto;}
.atrium-site .hero-copy h1{font-size:clamp(2.4rem, 4vw, 3.4rem);line-height:1.06;color:var(--paper);}
.atrium-site .hero-copy p{font-size:1.05rem;line-height:1.6;color:var(--smoke);max-width:440px;margin:0;}
.atrium-site .hero-actions{display:flex;gap:14px;flex-wrap:wrap;}
.atrium-site .search-card{margin-top:8px;background:var(--ink-2);border:1px solid var(--line-dark);border-radius:var(--radius);padding:18px;display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:0;}
.atrium-site .search-field{padding:6px 16px;border-right:1px solid var(--line-dark);}
.atrium-site .search-field:last-of-type{border-right:none;}
.atrium-site .search-field label{display:block;font-family:'IBM Plex Mono';font-size:0.66rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--smoke);margin-bottom:6px;}
.atrium-site .search-field select,.atrium-site .search-field input{background:none;border:none;color:var(--paper);font-family:'Archivo';font-size:0.92rem;width:100%;outline:none;}
.atrium-site .search-btn{background:var(--verdigris);border:none;color:var(--ink);padding:0 22px;font-family:'IBM Plex Mono';font-size:0.8rem;letter-spacing:0.04em;border-radius:var(--radius);cursor:pointer;}
.atrium-site .hero-art{position:relative;overflow:hidden;border-left:1px solid var(--line-dark);}
.atrium-site .hero-art svg{width:100%;height:100%;display:block;}
.atrium-site .sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:44px;}
.atrium-site .sec-head h2{font-size:clamp(1.7rem,2.6vw,2.3rem);}
.atrium-site .sec-head .view-all{font-family:'IBM Plex Mono';font-size:0.8rem;letter-spacing:0.04em;text-decoration:none;color:var(--verdigris-dim);border-bottom:1px solid currentColor;padding-bottom:2px;white-space:nowrap;}
.atrium-site .listings{padding:104px 0 96px;}
.atrium-site .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;}
.atrium-site .card{background:var(--linen);border:1px solid var(--line-light);border-radius:var(--radius);overflow:hidden;transition:transform .3s ease, box-shadow .3s ease;text-decoration:none;color:inherit;display:block;}
.atrium-site .card:hover{transform:translateY(-4px);box-shadow:0 18px 32px -18px rgba(20,23,26,0.28);}
.atrium-site .card-media{position:relative;aspect-ratio:4/3;overflow:hidden;background:var(--ink);}
.atrium-site .card-media svg,.atrium-site .card-media img{width:100%;height:100%;object-fit:cover;transition:transform .5s ease;}
.atrium-site .card:hover .card-media svg,.atrium-site .card:hover .card-media img{transform:scale(1.06);}
.atrium-site .tag{position:absolute;top:14px;left:14px;background:var(--paper);color:var(--ink);font-family:'IBM Plex Mono';font-size:0.66rem;letter-spacing:0.08em;text-transform:uppercase;padding:5px 10px;border-radius:var(--radius);}
.atrium-site .card-body{padding:20px 20px 22px;}
.atrium-site .card-price{font-family:'IBM Plex Mono';font-size:1.28rem;color:var(--ink);font-weight:500;}
.atrium-site .card-title{font-family:'Fraunces';font-size:1.05rem;margin:8px 0 4px;color:var(--ink);}
.atrium-site .card-loc{font-size:0.86rem;color:var(--verdigris-dim);margin-bottom:14px;}
.atrium-site .card-meta{display:flex;gap:16px;border-top:1px solid var(--line-light);padding-top:12px;}
.atrium-site .card-meta span{display:flex;align-items:center;gap:6px;font-family:'IBM Plex Mono';font-size:0.78rem;color:var(--graphite);}
.atrium-site .card-meta svg{width:14px;height:14px;stroke:var(--verdigris-dim);fill:none;}
.atrium-site .values{background:var(--ink);color:var(--paper);padding:96px 0;}
.atrium-site .values .eyebrow{color:var(--verdigris);}
.atrium-site .values-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--line-dark);margin-top:48px;border:1px solid var(--line-dark);}
.atrium-site .value-item{background:var(--ink);padding:36px 34px;}
.atrium-site .value-item .icon{width:34px;height:34px;stroke:var(--verdigris);fill:none;margin-bottom:22px;}
.atrium-site .value-item h3{color:var(--paper);font-size:1.15rem;margin-bottom:10px;}
.atrium-site .value-item p{font-size:0.92rem;line-height:1.6;color:var(--smoke);margin:0;}
.atrium-site .stats{padding:76px 0;border-bottom:1px solid var(--line-light);border-top:1px solid var(--line-light);}
.atrium-site .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);}
.atrium-site .stat{text-align:center;border-right:1px solid var(--line-light);padding:0 12px;}
.atrium-site .stat:last-child{border-right:none;}
.atrium-site .stat .num{font-family:'Fraunces';font-size:clamp(2rem,3.4vw,2.8rem);color:var(--ink);}
.atrium-site .stat .lbl{font-family:'IBM Plex Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--verdigris-dim);margin-top:8px;}
.atrium-site .about-preview,.atrium-site .about-full{padding:104px 0;}
.atrium-site .about-grid{display:grid;grid-template-columns:0.85fr 1fr;gap:64px;align-items:center;}
.atrium-site .about-art{aspect-ratio:5/4;background:var(--linen);border-radius:var(--radius);border:1px solid var(--line-light);overflow:hidden;}
.atrium-site .about-art svg{width:100%;height:100%;}
.atrium-site .about-copy p{font-size:1.02rem;line-height:1.7;color:var(--graphite);margin:22px 0 28px;max-width:480px;}
.atrium-site .link-arrow{display:inline-flex;align-items:center;gap:8px;text-decoration:none;color:var(--ink);font-family:'IBM Plex Mono';font-size:0.85rem;border-bottom:1px solid var(--ink);padding-bottom:3px;}
.atrium-site .testimonial{background:var(--linen);padding:110px 0;text-align:center;}
.atrium-site .testimonial blockquote{font-family:'Fraunces';font-weight:400;font-size:clamp(1.5rem,2.6vw,2.1rem);line-height:1.4;color:var(--ink);max-width:820px;margin:0 auto;font-style:italic;}
.atrium-site .testimonial cite{display:block;margin-top:28px;font-family:'IBM Plex Mono';font-style:normal;font-size:0.82rem;color:var(--verdigris-dim);}
.atrium-site .cta{background:var(--verdigris-dim);color:var(--paper);padding:88px 0;text-align:center;}
.atrium-site .cta h2{color:var(--paper);font-size:clamp(1.8rem,3vw,2.5rem);margin-bottom:16px;}
.atrium-site .cta p{color:rgba(243,239,230,0.85);max-width:480px;margin:0 auto 32px;}
.atrium-site .cta .btn{background:var(--paper);color:var(--ink);border-color:var(--paper);}
.atrium-site .cta .btn:hover{background:var(--ink);color:var(--paper);border-color:var(--ink);}
.atrium-site footer{background:var(--ink);color:var(--smoke);padding:72px 0 28px;}
.atrium-site .foot-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:40px;padding-bottom:48px;border-bottom:1px solid var(--line-dark);}
.atrium-site .foot-grid h4{font-family:'IBM Plex Mono';font-size:0.72rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--paper);margin-bottom:18px;}
.atrium-site .foot-grid ul{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:10px;}
.atrium-site .foot-grid a{text-decoration:none;color:var(--smoke);font-size:0.9rem;}
.atrium-site .foot-grid a:hover{color:var(--verdigris);}
.atrium-site .foot-logo{font-family:'Fraunces';font-size:1.4rem;color:var(--paper);margin-bottom:14px;}
.atrium-site .foot-bottom{display:flex;justify-content:space-between;padding-top:24px;font-size:0.78rem;font-family:'IBM Plex Mono';color:#6D7376;}
/* Form contatto (Contatti + dettaglio annuncio) */
.atrium-site .contact-page,.atrium-site .listing-detail-page{padding:80px 0 100px;}
.atrium-site .contact-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:56px;align-items:flex-start;}
.atrium-site .field-row{margin-bottom:18px;}
.atrium-site .field-row label{display:block;font-family:'IBM Plex Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--verdigris-dim);margin-bottom:8px;}
.atrium-site .field-row input,.atrium-site .field-row textarea{width:100%;background:var(--linen);border:1px solid var(--line-light);border-radius:var(--radius);padding:12px 14px;font-family:'Archivo';font-size:0.95rem;color:var(--ink);outline:none;transition:border-color .2s;}
.atrium-site .field-row input:focus,.atrium-site .field-row textarea:focus{border-color:var(--verdigris);}
.atrium-site .field-row textarea{min-height:130px;resize:vertical;}
.atrium-site .contact-side{background:var(--linen);border:1px solid var(--line-light);padding:32px 28px;}
.atrium-site .contact-side h3{margin-bottom:14px;font-size:1.15rem;}
.atrium-site .contact-side .row{display:flex;gap:10px;align-items:flex-start;margin:14px 0;font-size:0.92rem;color:var(--graphite);}
.atrium-site .contact-side .row svg{width:16px;height:16px;stroke:var(--verdigris-dim);fill:none;flex-shrink:0;margin-top:2px;}
.atrium-site .contact-map{margin-top:24px;aspect-ratio:16/10;background:var(--paper);border:1px solid var(--line-light);overflow:hidden;}
.atrium-site .contact-map iframe{width:100%;height:100%;border:none;}
/* Filtri annunci */
.atrium-site .filters-bar{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;padding:18px;background:var(--linen);border:1px solid var(--line-light);border-radius:var(--radius);}
.atrium-site .filters-bar select,.atrium-site .filters-bar input{background:var(--paper);border:1px solid var(--line-light);color:var(--ink);padding:10px 12px;font-family:'Archivo';font-size:0.9rem;border-radius:var(--radius);outline:none;}
/* Dettaglio annuncio */
.atrium-site .listing-hero{aspect-ratio:16/9;background:var(--ink);overflow:hidden;margin-bottom:36px;border:1px solid var(--line-light);}
.atrium-site .listing-hero svg,.atrium-site .listing-hero img{width:100%;height:100%;object-fit:cover;}
.atrium-site .listing-detail-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:48px;}
.atrium-site .listing-info-panel{background:var(--linen);border:1px solid var(--line-light);padding:28px 28px;position:sticky;top:100px;}
.atrium-site .listing-info-panel .price{font-family:'IBM Plex Mono';font-size:1.85rem;color:var(--ink);font-weight:500;margin-bottom:8px;}
.atrium-site .listing-info-panel .stats-list{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:20px 0 24px;padding:16px 0;border-top:1px solid var(--line-light);border-bottom:1px solid var(--line-light);font-family:'IBM Plex Mono';font-size:0.82rem;color:var(--graphite);}
.atrium-site .listing-info-panel .stats-list .k{font-size:0.68rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--verdigris-dim);display:block;margin-bottom:4px;}
.atrium-site .back-link{display:inline-flex;align-items:center;gap:8px;font-family:'IBM Plex Mono';font-size:0.8rem;color:var(--verdigris-dim);text-decoration:none;margin-bottom:20px;}
.atrium-site .empty-state{padding:80px 20px;text-align:center;color:var(--verdigris-dim);font-family:'IBM Plex Mono';font-size:0.9rem;}
/* Team page */
.atrium-site .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px;margin-top:44px;}
.atrium-site .team-card{background:var(--linen);border:1px solid var(--line-light);padding:24px;}
.atrium-site .team-card .avatar{aspect-ratio:1;background:var(--paper);margin-bottom:16px;border:1px solid var(--line-light);display:flex;align-items:center;justify-content:center;font-family:'Fraunces';font-size:2.2rem;color:var(--verdigris-dim);}
.atrium-site .team-card h4{font-family:'Fraunces';font-size:1.1rem;color:var(--ink);margin:0 0 4px;}
.atrium-site .team-card .role{font-family:'IBM Plex Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--verdigris-dim);}
@media (max-width: 980px){
  .atrium-site .hero{grid-template-columns:1fr;min-height:auto;}
  .atrium-site .hero-art{height:340px;order:-1;border-left:none;border-bottom:1px solid var(--line-dark);}
  .atrium-site .hero-copy{margin:0;max-width:100%;padding:56px 24px;}
  .atrium-site .search-card{grid-template-columns:1fr 1fr;row-gap:14px;}
  .atrium-site .search-field{border-right:none;}
  .atrium-site .search-btn{grid-column:1/-1;padding:12px;}
  .atrium-site .navlinks{display:none;}
  .atrium-site .grid-3,.atrium-site .team-grid{grid-template-columns:1fr;}
  .atrium-site .values-grid{grid-template-columns:1fr;}
  .atrium-site .about-grid,.atrium-site .contact-grid,.atrium-site .listing-detail-grid{grid-template-columns:1fr;gap:36px;}
  .atrium-site .stats-grid{grid-template-columns:1fr 1fr;row-gap:32px;}
  .atrium-site .stat{border-right:none;}
  .atrium-site .foot-grid{grid-template-columns:1fr 1fr;row-gap:32px;}
  .atrium-site .listing-info-panel{position:static;}
}
`;

// ============================================================================
//  SVG placeholder helper (usato quando manca cover_url)
// ============================================================================
function PropertyPlaceholder({ variant = 0 }) {
  const strokes = ["#6E8C7C", "#A9744F", "#6E8C7C"];
  const stroke = strokes[variant % 3];
  return (
    <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="300" fill="#14171A" />
      <g stroke={stroke} strokeWidth="1.2" fill="none" opacity="0.85">
        <path d="M40 220 L40 130 L200 60 L360 130 L360 220 Z" />
        <rect x="90" y="150" width="50" height="70" />
        <rect x="170" y="150" width="60" height="70" />
        <rect x="260" y="150" width="50" height="70" />
        <path d="M20 220 L380 220" />
      </g>
    </svg>
  );
}

function HeroArt() {
  return (
    <svg viewBox="0 0 600 640" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="600" height="640" fill="#1D2124" />
      <g stroke="#6E8C7C" strokeWidth="1.4" fill="none" opacity="0.9">
        <path d="M40 460 L40 260 L300 120 L560 260 L560 460 Z" />
        <path d="M40 260 L560 260" />
        <path d="M300 120 L300 460" />
        <rect x="90" y="300" width="70" height="90" />
        <rect x="190" y="300" width="70" height="160" />
        <rect x="340" y="300" width="70" height="160" />
        <rect x="440" y="300" width="70" height="90" />
        <path d="M40 460 L560 460" />
        <path d="M0 520 L600 520" opacity="0.4" />
      </g>
      <circle cx="300" cy="460" r="3" fill="#A9744F" opacity="0.85" />
    </svg>
  );
}

// ============================================================================
//  Header / Footer condivisi
// ============================================================================
function AtriumHeader({ slug, studio, activePage }) {
  const base = `/site/${slug}`;
  return (
    <header data-testid="atrium-header">
      <div className="nav">
        <Link to={base} className="logo" data-testid="atrium-logo">
          {(studio.name || "ATRIUM").toUpperCase()}<span>.</span>
        </Link>
        <ul className="navlinks">
          <li><Link to={base} className={activePage === "home" ? "active" : ""} data-testid="atrium-nav-home">Home</Link></li>
          <li><Link to={`${base}/annunci`} className={activePage === "annunci" ? "active" : ""} data-testid="atrium-nav-annunci">Annunci</Link></li>
          <li><Link to={`${base}/chi-siamo`} className={activePage === "chi-siamo" ? "active" : ""} data-testid="atrium-nav-chi-siamo">Chi siamo</Link></li>
          <li><Link to={`${base}/contatti`} className={activePage === "contatti" ? "active" : ""} data-testid="atrium-nav-contatti">Contatti</Link></li>
        </ul>
        <Link to={`${base}/contatti`} className="btn" data-testid="atrium-cta-header">Richiedi valutazione</Link>
      </div>
    </header>
  );
}

function AtriumFooter({ slug, studio }) {
  const base = `/site/${slug}`;
  const year = new Date().getFullYear();
  return (
    <footer data-testid="atrium-footer">
      <div className="wrap">
        <div className="foot-grid">
          <div>
            <div className="foot-logo">{(studio.name || "ATRIUM").toUpperCase()}.</div>
            <p style={{fontSize:"0.88rem",lineHeight:1.6,maxWidth:"260px",color:"#9AA3A0"}}>
              {studio.tagline || `Agenzia immobiliare${studio.city ? " a " + studio.city : ""}${studio.founded_year ? " dal " + studio.founded_year : ""}.`}
            </p>
          </div>
          <div>
            <h4>Naviga</h4>
            <ul>
              <li><Link to={base}>Home</Link></li>
              <li><Link to={`${base}/annunci`}>Annunci</Link></li>
              <li><Link to={`${base}/chi-siamo`}>Chi siamo</Link></li>
              <li><Link to={`${base}/contatti`}>Contatti</Link></li>
            </ul>
          </div>
          <div>
            <h4>Contatti</h4>
            <ul>
              {studio.phone && <li><a href={`tel:${studio.phone}`}>{studio.phone}</a></li>}
              {studio.email && <li><a href={`mailto:${studio.email}`}>{studio.email}</a></li>}
              {studio.address && <li>{studio.address}</li>}
            </ul>
          </div>
          <div>
            <h4>Seguici</h4>
            <ul>
              <li><a href="#" onClick={(e)=>e.preventDefault()}>Instagram</a></li>
              <li><a href="#" onClick={(e)=>e.preventDefault()}>LinkedIn</a></li>
              <li><a href="#" onClick={(e)=>e.preventDefault()}>Facebook</a></li>
            </ul>
          </div>
        </div>
        <div className="foot-bottom">
          <span>© {year} {studio.name || "Atrium Immobiliare"}</span>
          {studio.vat && <span>P.IVA {studio.vat}</span>}
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
//  Property Card (usata in Home listings + Annunci)
// ============================================================================
function PropertyCard({ p, slug, index = 0, tagOverride }) {
  const priceFmt = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <Link to={`/site/${slug}/annunci/${p.id}`} className="card" data-testid={`atrium-property-card-${p.id}`}>
      <div className="card-media">
        <span className="tag">{tagOverride || p.tag || "In vendita"}</span>
        {p.cover_url ? <img src={p.cover_url} alt={p.address || "immobile"} /> : <PropertyPlaceholder variant={index} />}
      </div>
      <div className="card-body">
        <div className="card-price">{priceFmt}</div>
        <h3 className="card-title">{p.address || "Immobile"}</h3>
        <div className="card-loc">{p.city || ""}</div>
        <div className="card-meta">
          {p.surface_m2 ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="1"/></svg>{p.surface_m2} mq</span> : null}
          {p.rooms ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 20h16M6 20V10l6-4 6 4v10"/></svg>{p.rooms} locali</span> : null}
          {p.bathrooms ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><circle cx="12" cy="12" r="9"/></svg>{p.bathrooms} bagni</span> : null}
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
//  Home page sections
// ============================================================================
function HomePage({ slug, studio, siteConfig, properties }) {
  const nomeAgenzia = studio.name || "Atrium";
  const annoFondazione = studio.founded_year || "";
  return (
    <>
      <Removable sectionKey="hero">
      <section className="hero" data-testid="atrium-section-hero">
        <div className="hero-copy">
          <p className="eyebrow">
            <Editable field="hero.eyebrow">Agenzia immobiliare{annoFondazione ? ` — dal ${annoFondazione}` : ""}</Editable>
          </p>
          <Editable field="hero.h1" as="h1" multiline>Case che raccontano una storia.</Editable>
          <Editable field="hero.p" as="p" multiline>Selezioniamo immobili con carattere e li accompagniamo, dalla prima visita al rogito, con la cura di chi conosce ogni via del quartiere.</Editable>
          <div className="hero-actions">
            <Link to={`/site/${slug}/annunci`} className="btn" data-testid="atrium-hero-cta-annunci"><Editable field="hero.cta1">Vedi gli annunci</Editable></Link>
            <Link to={`/site/${slug}/contatti`} className="btn btn-ghost" data-testid="atrium-hero-cta-vendi"><Editable field="hero.cta2">Vendi il tuo immobile</Editable></Link>
          </div>
          <div className="search-card">
            <div className="search-field">
              <label>Città</label>
              <input placeholder={studio.city || "Bologna"} defaultValue={studio.city || ""} />
            </div>
            <div className="search-field">
              <label>Tipologia</label>
              <select defaultValue=""><option value="">Tutte</option><option>Appartamento</option><option>Villa</option><option>Attico</option></select>
            </div>
            <div className="search-field">
              <label>Budget</label>
              <select defaultValue=""><option value="">Tutti</option><option>Fino a 300k</option><option>300k–600k</option><option>600k+</option></select>
            </div>
            <Link to={`/site/${slug}/annunci`} className="search-btn" data-testid="atrium-hero-search-btn">Cerca →</Link>
          </div>
        </div>
        <div className="hero-art"><HeroArt/></div>
      </section>
      </Removable>

      {properties?.length > 0 && (
        <Removable sectionKey="listings">
        <section className="listings" data-testid="atrium-section-listings">
          <div className="wrap">
            <div className="sec-head">
              <div>
                <p className="eyebrow">In vetrina</p>
                <h2>Immobili selezionati questa settimana</h2>
              </div>
              <Link to={`/site/${slug}/annunci`} className="view-all">Tutti gli annunci →</Link>
            </div>
            <div className="grid-3">
              {properties.slice(0, 3).map((p, i) => <PropertyCard key={p.id} p={p} slug={slug} index={i} tagOverride={i === 0 ? "In esclusiva" : (i === 1 ? "Nuovo" : "Ristrutturato")} />)}
            </div>
          </div>
        </section>
        </Removable>
      )}

      <Removable sectionKey="values">
      <section className="values" data-testid="atrium-section-values">
        <div className="wrap">
          <p className="eyebrow">Il nostro metodo</p>
          <h2>Perché scegliere {nomeAgenzia}</h2>
          <div className="values-grid">
            <div className="value-item">
              <svg className="icon" viewBox="0 0 24 24" strokeWidth="1.4"><path d="M12 2 3 7v13h6v-6h6v6h6V7z"/></svg>
              <h3>Conoscenza del territorio</h3>
              <p>{annoFondazione ? (2026 - Number(annoFondazione)) : "Molti"} anni di attività{studio.city ? ` a ${studio.city}` : ""}: sappiamo cosa vale davvero ogni indirizzo.</p>
            </div>
            <div className="value-item">
              <svg className="icon" viewBox="0 0 24 24" strokeWidth="1.4"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.5-6 8-6s8 2 8 6"/></svg>
              <h3>Un referente unico</h3>
              <p>Segui il tuo immobile con una sola persona di riferimento, dalla valutazione al rogito.</p>
            </div>
            <div className="value-item">
              <svg className="icon" viewBox="0 0 24 24" strokeWidth="1.4"><path d="M4 4h16v4H4zM6 8v12h12V8"/></svg>
              <h3>Valutazioni gratuite</h3>
              <p>Stima trasparente e senza impegno, basata su dati reali di vendita della zona.</p>
            </div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="stats">
      <section className="stats" data-testid="atrium-section-stats">
        <div className="wrap">
          <div className="stats-grid">
            <div className="stat"><div className="num mono">312</div><div className="lbl">Immobili venduti</div></div>
            <div className="stat"><div className="num mono">{annoFondazione ? (2026 - Number(annoFondazione)) : "—"}</div><div className="lbl">Anni di attività</div></div>
            <div className="stat"><div className="num mono">4.9</div><div className="lbl">Valutazione media</div></div>
            <div className="stat"><div className="num mono">48h</div><div className="lbl">Tempo di risposta</div></div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="about-preview">
      <section className="about-preview" data-testid="atrium-section-about-preview">
        <div className="wrap about-grid">
          <div className="about-art">
            <svg viewBox="0 0 400 320" preserveAspectRatio="xMidYMid slice">
              <rect width="400" height="320" fill="#E9E3D6"/>
              <g stroke="#3E4143" strokeWidth="1.3" fill="none" opacity="0.75">
                <circle cx="140" cy="140" r="46"/><path d="M94 220c0-30 20-46 46-46s46 16 46 46"/>
                <circle cx="270" cy="150" r="40"/><path d="M228 220c0-26 18-40 42-40s42 14 42 40"/>
              </g>
            </svg>
          </div>
          <div className="about-copy">
            <p className="eyebrow">Chi siamo</p>
            <h2>Un team di persone, una sola idea di ospitalità</h2>
            <p>{nomeAgenzia} nasce{annoFondazione ? ` nel ${annoFondazione}` : ""} come piccola agenzia di quartiere. Oggi siamo un team affiatato che continua a lavorare con lo stesso approccio artigianale: poche pratiche alla volta, seguite bene.</p>
            <Link to={`/site/${slug}/chi-siamo`} className="link-arrow">Scopri il nostro team →</Link>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="testimonial">
      <section className="testimonial" data-testid="atrium-section-testimonial">
        <div className="wrap">
          <Editable field="testimonial.quote" as="blockquote" multiline>«Ci hanno seguiti per otto mesi senza mai farci sentire un numero. Hanno trovato loro l'acquirente giusto, non il primo disponibile.»</Editable>
          <Editable field="testimonial.cite" as="cite">— Marta e Luca, venditori{studio.city ? ` a ${studio.city}` : ""}</Editable>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="cta">
      <section className="cta" data-testid="atrium-section-cta">
        <div className="wrap">
          <Editable field="cta.h2" as="h2">Pensi di vendere il tuo immobile?</Editable>
          <Editable field="cta.p" as="p" multiline>Richiedi una valutazione gratuita: un nostro consulente ti risponde entro 48 ore.</Editable>
          <Link to={`/site/${slug}/contatti`} className="btn" data-testid="atrium-cta-valutazione">
            <Editable field="cta.btn">Richiedi valutazione gratuita</Editable>
          </Link>
        </div>
      </section>
      </Removable>
    </>
  );
}

// ============================================================================
//  Annunci page (griglia completa con filtri)
// ============================================================================
function AnnunciPage({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/site/${slug}/properties`)
      .then((r) => setItems(r.data.properties || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const cities = useMemo(() => Array.from(new Set(items.map(i => i.city).filter(Boolean))), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (city && p.city !== city) return false;
    if (search && !(p.address || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, city, search]);

  return (
    <section className="listings" data-testid="atrium-page-annunci">
      <div className="wrap">
        <div className="sec-head">
          <div>
            <p className="eyebrow">Tutti gli immobili</p>
            <h2>Il nostro catalogo</h2>
          </div>
          <span className="view-all" data-testid="atrium-annunci-count">{filtered.length} immobil{filtered.length === 1 ? "e" : "i"}</span>
        </div>
        <div className="filters-bar" data-testid="atrium-annunci-filters">
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="atrium-filter-city">
            <option value="">Tutte le città</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Cerca per indirizzo…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="atrium-filter-search" style={{flex: 1, minWidth: "220px"}} />
        </div>
        {loading ? (
          <div className="empty-state">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="atrium-annunci-empty">Nessun immobile disponibile al momento.</div>
        ) : (
          <div className="grid-3">
            {filtered.map((p, i) => <PropertyCard key={p.id} p={p} slug={slug} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
//  Chi siamo page
// ============================================================================
function ChiSiamoPage({ slug, studio }) {
  const teamMembers = [
    { name: studio.name || "Il Team", role: "Fondatore", initial: (studio.name || "A")[0] },
    { name: "Sara Rossi", role: "Consulente Senior", initial: "S" },
    { name: "Marco Bianchi", role: "Valutatore", initial: "M" },
  ];
  return (
    <section className="about-full" data-testid="atrium-page-chi-siamo">
      <div className="wrap">
        <p className="eyebrow">Chi siamo</p>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"24px"}}>La nostra storia</h2>
        <p style={{fontSize:"1.05rem",lineHeight:1.7,maxWidth:"760px",color:"#3E4143",marginBottom:"48px"}}>
          {studio.name || "Atrium"} nasce{studio.founded_year ? ` nel ${studio.founded_year}` : ""}{studio.city ? ` a ${studio.city}` : ""} come piccola agenzia di quartiere. Oggi siamo un team affiatato che continua a lavorare con lo stesso approccio artigianale: poche pratiche alla volta, seguite bene, con un referente unico che ti accompagna dalla prima visita al rogito.
        </p>

        <p className="eyebrow" style={{marginTop:"48px"}}>Il nostro team</p>
        <div className="team-grid">
          {teamMembers.map((m, i) => (
            <div className="team-card" key={i} data-testid={`atrium-team-${i}`}>
              <div className="avatar">{m.initial}</div>
              <h4>{m.name}</h4>
              <div className="role">{m.role}</div>
            </div>
          ))}
        </div>

        <p className="eyebrow" style={{marginTop:"72px"}}>I nostri valori</p>
        <div className="values-grid" style={{marginTop:"24px",background:"#D9D2C1",borderColor:"#D9D2C1"}}>
          <div className="value-item" style={{background:"#F3EFE6"}}>
            <h3 style={{color:"#14171A"}}>Trasparenza</h3>
            <p style={{color:"#3E4143"}}>Ogni valutazione è motivata con dati reali. Zero prezzi gonfiati per prendere l'incarico.</p>
          </div>
          <div className="value-item" style={{background:"#F3EFE6"}}>
            <h3 style={{color:"#14171A"}}>Cura artigianale</h3>
            <p style={{color:"#3E4143"}}>Seguiamo poche pratiche alla volta per garantire attenzione autentica a ogni cliente.</p>
          </div>
          <div className="value-item" style={{background:"#F3EFE6"}}>
            <h3 style={{color:"#14171A"}}>Radici nel quartiere</h3>
            <p style={{color:"#3E4143"}}>Conosciamo{studio.city ? ` ${studio.city}` : " il territorio"} strada per strada. Sappiamo cosa vale davvero.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Contact form (usato in /contatti + dettaglio annuncio)
// ============================================================================
function ContactForm({ slug, propertyId, testIdPrefix = "atrium-contact" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nome, email e messaggio sono obbligatori.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/site/${slug}/contact`, { ...form, property_id: propertyId || null });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Messaggio inviato — ti risponderemo entro 48 ore.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito, riprova.");
    } finally {
      setSending(false);
    }
  };
  if (sent) {
    return (
      <div className="contact-side" data-testid={`${testIdPrefix}-success`} style={{background:"var(--verdigris-dim)",color:"var(--paper)",borderColor:"var(--verdigris-dim)"}}>
        <h3 style={{color:"var(--paper)"}}>Grazie! Il tuo messaggio è stato inviato.</h3>
        <p style={{fontSize:"0.95rem",lineHeight:1.6,color:"rgba(243,239,230,0.85)"}}>Un consulente ti risponderà entro 48 ore all'indirizzo che ci hai fornito.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`}>
      <div className="field-row"><label>Nome e cognome</label><input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} data-testid={`${testIdPrefix}-name`} required /></div>
      <div className="field-row"><label>Email</label><input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} data-testid={`${testIdPrefix}-email`} required /></div>
      <div className="field-row"><label>Telefono (opzionale)</label><input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} data-testid={`${testIdPrefix}-phone`} /></div>
      <div className="field-row"><label>Messaggio</label><textarea value={form.message} onChange={(e)=>setForm(f=>({...f, message:e.target.value}))} data-testid={`${testIdPrefix}-message`} required /></div>
      <button type="submit" className="btn" disabled={sending} data-testid={`${testIdPrefix}-submit`}>{sending ? "Invio…" : "Invia richiesta"}</button>
    </form>
  );
}

// ============================================================================
//  Contatti page
// ============================================================================
function ContattiPage({ slug, studio }) {
  const mapQuery = encodeURIComponent(studio.address || studio.city || "Bologna");
  return (
    <section className="contact-page" data-testid="atrium-page-contatti">
      <div className="wrap">
        <p className="eyebrow">Contattaci</p>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"48px"}}>Parliamone.</h2>
        <div className="contact-grid">
          <div>
            <ContactForm slug={slug} testIdPrefix="atrium-contatti" />
          </div>
          <aside className="contact-side" data-testid="atrium-contatti-side">
            <h3>Recapiti</h3>
            {studio.phone && (
              <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>{studio.phone}</div>
            )}
            {studio.email && (
              <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>{studio.email}</div>
            )}
            {studio.address && (
              <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{studio.address}</div>
            )}
            <div className="contact-map" data-testid="atrium-contatti-map">
              <iframe title="Mappa" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy" />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Dettaglio annuncio + form contatto
// ============================================================================
function ListingDetailPage({ slug, pid }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/site/${slug}/properties/${pid}`)
      .then((r) => setProp(r.data.property))
      .catch(() => setProp(null))
      .finally(() => setLoading(false));
  }, [slug, pid]);

  if (loading) return <section className="listing-detail-page"><div className="wrap empty-state">Caricamento immobile…</div></section>;
  if (!prop) return <section className="listing-detail-page"><div className="wrap empty-state" data-testid="atrium-listing-detail-not-found">Immobile non trovato o non più disponibile.</div></section>;

  const priceFmt = prop.price ? `€ ${Number(prop.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <section className="listing-detail-page" data-testid="atrium-page-listing-detail">
      <div className="wrap">
        <Link to={`/site/${slug}/annunci`} className="back-link" data-testid="atrium-listing-back">← Torna agli annunci</Link>
        <div className="listing-hero">
          {prop.cover_url ? <img src={prop.cover_url} alt={prop.address}/> : <PropertyPlaceholder variant={0}/>}
        </div>
        <div className="listing-detail-grid">
          <div>
            <p className="eyebrow">{prop.tag || "In vendita"}</p>
            <h2 style={{fontSize:"clamp(1.6rem,2.8vw,2.3rem)",marginTop:"10px"}}>{prop.address || "Immobile"}</h2>
            <p style={{color:"var(--verdigris-dim)",fontSize:"1rem",marginTop:"6px"}}>{prop.city}</p>
            {prop.description && <p style={{fontSize:"1rem",lineHeight:1.7,marginTop:"32px",color:"var(--graphite)"}}>{prop.description}</p>}
          </div>
          <aside className="listing-info-panel" data-testid="atrium-listing-info-panel">
            <div className="price">{priceFmt}</div>
            <div className="stats-list">
              {prop.surface_m2 ? <div><span className="k">Superficie</span>{prop.surface_m2} mq</div> : null}
              {prop.rooms ? <div><span className="k">Locali</span>{prop.rooms}</div> : null}
              {prop.bathrooms ? <div><span className="k">Bagni</span>{prop.bathrooms}</div> : null}
              {prop.city ? <div><span className="k">Zona</span>{prop.city}</div> : null}
            </div>
            <h3 style={{fontSize:"1.05rem",marginBottom:"18px"}}>Richiedi informazioni</h3>
            <ContactForm slug={slug} propertyId={pid} testIdPrefix="atrium-listing-contact" />
          </aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Root — orchestratore + fetch + routing interno
// ============================================================================
export default function AtriumSite({ initialData, slug: slugProp }) {
  const paramsSlug = useParams().slug;
  const slug = slugProp || paramsSlug;
  const location = useSiteLocation();
  const navigate = useSiteNavigate();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialData || !slug) return;
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, initialData]);

  // Sub-path parsing (siamo dentro /site/:slug/*)
  const subPath = location.pathname.replace(`/site/${slug}`, "").replace(/^\//, "");
  const [seg1, seg2] = subPath.split("/");

  if (loading) {
    return (
      <div className="atrium-site">
        <style>{ATRIUM_CSS}</style>
        <div className="empty-state" style={{padding:"120px 20px"}}>Caricamento sito…</div>
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="atrium-site" data-testid="atrium-not-found">
        <style>{ATRIUM_CSS}</style>
        <div className="empty-state" style={{padding:"120px 20px"}}>
          Sito non trovato. <button onClick={() => navigate("/")} style={{background:"none",border:"none",color:"var(--verdigris)",cursor:"pointer",textDecoration:"underline"}}>Torna alla homepage</button>
        </div>
      </div>
    );
  }

  const { studio, site_config: siteConfig, featured_properties: properties } = data;
  let activePage = "home";
  if (seg1 === "annunci") activePage = "annunci";
  else if (seg1 === "chi-siamo") activePage = "chi-siamo";
  else if (seg1 === "contatti") activePage = "contatti";

  return (
    <div className="atrium-site" data-testid="atrium-site-root" data-slug={slug}>
      <style>{ATRIUM_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,340;9..144,480;9..144,600&family=Archivo:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet"/>
      <AtriumHeader slug={slug} studio={studio} activePage={activePage} />
      {seg1 === "" || seg1 === undefined ? (
        <HomePage slug={slug} studio={studio} siteConfig={siteConfig} properties={properties} />
      ) : seg1 === "annunci" && !seg2 ? (
        <AnnunciPage slug={slug} />
      ) : seg1 === "annunci" && seg2 ? (
        <ListingDetailPage slug={slug} pid={seg2} />
      ) : seg1 === "chi-siamo" ? (
        <ChiSiamoPage slug={slug} studio={studio} />
      ) : seg1 === "contatti" ? (
        <ContattiPage slug={slug} studio={studio} />
      ) : (
        <div className="empty-state" style={{padding:"120px 20px"}}>Pagina non trovata.</div>
      )}
      <AtriumFooter slug={slug} studio={studio} />
    </div>
  );
}
